import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT, mergeContent } from "@/lib/siteContent";
import CourseSelection from "@/pages/CourseSelection";
import {
  Loader2, Save, Eye, Rocket, Plus, Trash2, X, AlertTriangle, CheckCircle2,
  Smartphone, Monitor, RotateCcw,
} from "lucide-react";

/**
 * Website Editor (CMS) tab for the admin dashboard.
 * Loads draft + published content, lets the admin edit each section,
 * save-as-draft, preview in modal, or publish with confirmation.
 */
export default function SiteEditor() {
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);       // editable working copy
  const [publishedAt, setPublishedAt] = useState(null);
  const [draftUpdatedAt, setDraftUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [banner, setBanner] = useState(null);      // { kind: "success"|"error", text }
  const [showPreview, setShowPreview] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("mobile"); // future toggle

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/site-content");
        const base = data.draft || data.published || {};
        // Merge defaults so all fields exist in the form.
        if (!cancelled) {
          setDraft(mergeContent(DEFAULT_CONTENT, base));
          setPublishedAt(data.published?.published_at || null);
          setDraftUpdatedAt(data.draft?.updated_at || null);
        }
      } catch (err) {
        if (!cancelled) {
          setBanner({ kind: "error", text: err?.response?.data?.detail || "Kon content niet laden." });
          setDraft(mergeContent(DEFAULT_CONTENT, {}));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setField = (path, value) => {
    setDraft((d) => {
      const next = structuredClone(d);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    setBanner(null);
    try {
      const payload = pickPayload(draft);
      const { data } = await api.put("/admin/site-content/draft", payload);
      setDraftUpdatedAt(data.draft?.updated_at || new Date().toISOString());
      setBanner({ kind: "success", text: "Concept opgeslagen." });
    } catch (err) {
      setBanner({ kind: "error", text: err?.response?.data?.detail || "Opslaan mislukt." });
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    setBanner(null);
    try {
      // Always save the latest draft first so publish uses the current values.
      await api.put("/admin/site-content/draft", pickPayload(draft));
      const { data } = await api.post("/admin/site-content/publish");
      setPublishedAt(data.published_at);
      setShowPublishConfirm(false);
      setBanner({ kind: "success", text: "Live gezet op de website." });
    } catch (err) {
      setBanner({ kind: "error", text: err?.response?.data?.detail || "Publiceren mislukt." });
    } finally {
      setPublishing(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm("Alle wijzigingen in dit concept overschrijven met de standaardteksten?")) return;
    setDraft(structuredClone(DEFAULT_CONTENT));
  };

  if (loading || !draft) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400">
        <Loader2 className="animate-spin mr-2" /> Content laden…
      </div>
    );
  }

  return (
    <div data-testid="site-editor">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 -mx-4 sm:mx-0 mb-5 bg-neutral-950/95 backdrop-blur border-b border-neutral-800 px-4 sm:px-5 py-3 flex flex-wrap items-center gap-2 sm:rounded-2xl">
        <div className="mr-auto min-w-0">
          <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500">Website Editor</div>
          <div className="text-xs text-neutral-400 mt-0.5">
            {publishedAt ? <>Live sinds <span className="text-neutral-200">{fmtDate(publishedAt)}</span></> : "Nog niet gepubliceerd"}
            {draftUpdatedAt && <> · Concept: <span className="text-neutral-200">{fmtDate(draftUpdatedAt)}</span></>}
          </div>
        </div>
        <button onClick={resetToDefaults} className="text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-300 px-3 py-2 rounded-full flex items-center gap-1.5" data-testid="editor-reset">
          <RotateCcw size={12} /> Reset
        </button>
        <button onClick={() => setShowPreview(true)} className="text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-100 px-3 py-2 rounded-full flex items-center gap-1.5" data-testid="editor-preview">
          <Eye size={12} /> Preview
        </button>
        <button onClick={saveDraft} disabled={saving} className="text-xs bg-white text-black font-semibold px-3 py-2 rounded-full flex items-center gap-1.5 disabled:opacity-50" data-testid="editor-save-draft">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Opslaan als concept
        </button>
        <button onClick={() => setShowPublishConfirm(true)} className="text-xs bg-[#FF6A1A] text-black font-bold px-3 py-2 rounded-full flex items-center gap-1.5" data-testid="editor-publish">
          <Rocket size={12} /> Publiceren
        </button>
      </div>

      {banner && (
        <div className={`mb-4 rounded-xl px-4 py-3 flex items-start gap-2 text-sm ${
          banner.kind === "success" ? "bg-green-900/30 border border-green-700/40 text-green-200" : "bg-red-900/30 border border-red-700/40 text-red-200"
        }`} data-testid="editor-banner">
          {banner.kind === "success" ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertTriangle size={16} className="mt-0.5" />}
          <span className="flex-1">{banner.text}</span>
          <button onClick={() => setBanner(null)} className="opacity-70 hover:opacity-100"><X size={14} /></button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {/* Announcement banner */}
        <Section title="Aankondiging (banner bovenaan)" testid="section-announcement">
          <div className="flex items-center gap-3 mb-3">
            <Toggle
              checked={!!draft.announcement?.enabled}
              onChange={(v) => setField(["announcement", "enabled"], v)}
              testid="announcement-enabled"
            />
            <span className="text-sm">Banner tonen op homepage</span>
          </div>
          <Field label="Tekst">
            <input
              type="text"
              value={draft.announcement?.text || ""}
              onChange={(e) => setField(["announcement", "text"], e.target.value)}
              placeholder="Bijv: Nog 2 plekken over voor juli — reageer snel."
              className={inputCls}
              data-testid="announcement-text"
            />
          </Field>
        </Section>

        {/* Hero */}
        <Section title="Hero (bovenkant homepage)" testid="section-hero">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Label boven titel">
              <input type="text" className={inputCls} value={draft.hero.label || ""} onChange={(e) => setField(["hero", "label"], e.target.value)} data-testid="hero-label" />
            </Field>
            <Field label="Urgentie-tekst  (gebruik {n} voor plek-teller)">
              <input type="text" className={inputCls} value={draft.hero.urgency_template || ""} onChange={(e) => setField(["hero", "urgency_template"], e.target.value)} data-testid="hero-urgency" />
            </Field>
            <Field label="Titel — regel 1">
              <input type="text" className={inputCls} value={draft.hero.title_line1 || ""} onChange={(e) => setField(["hero", "title_line1"], e.target.value)} data-testid="hero-title-line1" />
            </Field>
            <Field label="Titel — cursief accent">
              <input type="text" className={inputCls} value={draft.hero.title_italic || ""} onChange={(e) => setField(["hero", "title_italic"], e.target.value)} data-testid="hero-title-italic" />
            </Field>
            <Field label="Titel — regel 2" full>
              <input type="text" className={inputCls} value={draft.hero.title_line2 || ""} onChange={(e) => setField(["hero", "title_line2"], e.target.value)} data-testid="hero-title-line2" />
            </Field>
            <Field label="Subtitel  (gebruik {n} voor plek-teller)" full>
              <textarea rows={2} className={inputCls} value={draft.hero.subtitle || ""} onChange={(e) => setField(["hero", "subtitle"], e.target.value)} data-testid="hero-subtitle" />
            </Field>
            <Field label="Primaire knoptekst">
              <input type="text" className={inputCls} value={draft.hero.primary_cta || ""} onChange={(e) => setField(["hero", "primary_cta"], e.target.value)} data-testid="hero-primary-cta" />
            </Field>
            <Field label="Secundaire knoptekst (WhatsApp)">
              <input type="text" className={inputCls} value={draft.hero.secondary_cta || ""} onChange={(e) => setField(["hero", "secondary_cta"], e.target.value)} data-testid="hero-secondary-cta" />
            </Field>
          </div>
          <div className="mt-4">
            <div className="text-xs text-neutral-400 mb-2">Trust bullets (4 stuks)</div>
            <StringList
              items={draft.hero.trust_bullets || []}
              onChange={(list) => setField(["hero", "trust_bullets"], list)}
              placeholder="Bijv: Kleine groepen"
              testid="hero-trust"
              max={4}
            />
          </div>
        </Section>

        {/* Courses */}
        <Section title="Cursussen (naam, prijs, beschikbare plekken)" testid="section-courses">
          <div className="space-y-4">
            {(draft.courses || []).map((c, idx) => (
              <div key={c.id} className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4" data-testid={`course-block-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wide text-[#FF6A1A]">{c.id}</div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs">
                      <Toggle
                        checked={!!c.visible}
                        onChange={(v) => setField(["courses", idx, "visible"], v)}
                        testid={`course-visible-${c.id}`}
                      />
                      Zichtbaar
                    </label>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Cursusnaam">
                    <input type="text" className={inputCls} value={c.name || ""} onChange={(e) => setField(["courses", idx, "name"], e.target.value)} data-testid={`course-name-${c.id}`} />
                  </Field>
                  <Field label="Subtitel">
                    <input type="text" className={inputCls} value={c.subtitle || ""} onChange={(e) => setField(["courses", idx, "subtitle"], e.target.value)} data-testid={`course-subtitle-${c.id}`} />
                  </Field>
                  <Field label="Datum-regel">
                    <input type="text" className={inputCls} value={c.date_line || ""} onChange={(e) => setField(["courses", idx, "date_line"], e.target.value)} data-testid={`course-dateline-${c.id}`} />
                  </Field>
                  <Field label="Prijs (€)">
                    <input type="number" className={inputCls} value={c.price ?? 0} onChange={(e) => setField(["courses", idx, "price"], Number(e.target.value))} data-testid={`course-price-${c.id}`} />
                  </Field>
                  <Field label="Totaal aantal plekken">
                    <input type="number" className={inputCls} value={c.total_spots ?? 0} onChange={(e) => setField(["courses", idx, "total_spots"], Number(e.target.value))} data-testid={`course-total-${c.id}`} />
                  </Field>
                  <Field label="Handmatig: nog beschikbaar (leeg = auto tellen)">
                    <input
                      type="number"
                      className={inputCls}
                      value={c.available_spots ?? ""}
                      onChange={(e) => setField(["courses", idx, "available_spots"], e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="auto"
                      data-testid={`course-available-${c.id}`}
                    />
                  </Field>
                  <Field label="Status">
                    <select className={inputCls} value={c.status || "open"} onChange={(e) => setField(["courses", idx, "status"], e.target.value)} data-testid={`course-status-${c.id}`}>
                      <option value="open">Open</option>
                      <option value="sold_out">Uitverkocht</option>
                    </select>
                  </Field>
                  <Field label="Knoptekst">
                    <input type="text" className={inputCls} value={c.cta_text || ""} onChange={(e) => setField(["courses", idx, "cta_text"], e.target.value)} data-testid={`course-cta-${c.id}`} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Sales texts */}
        <Section title="Verkoopteksten (Voor wie / Resultaat / Twijfel)" testid="section-sales">
          <Field label="Concreet resultaat (HTML toegestaan: <strong>, <em>)" full>
            <textarea rows={4} className={inputCls} value={draft.sales_texts?.outcome_html || ""} onChange={(e) => setField(["sales_texts", "outcome_html"], e.target.value)} data-testid="sales-outcome" />
          </Field>
          <Field label="Voor wie is dit? (HTML toegestaan)" full>
            <textarea rows={3} className={inputCls} value={draft.sales_texts?.for_who_html || ""} onChange={(e) => setField(["sales_texts", "for_who_html"], e.target.value)} data-testid="sales-forwho" />
          </Field>
          <Field label="Twijfel-opvanger tekst" full>
            <textarea rows={2} className={inputCls} value={draft.sales_texts?.doubt_text || ""} onChange={(e) => setField(["sales_texts", "doubt_text"], e.target.value)} data-testid="sales-doubt" />
          </Field>
          <div className="mt-2">
            <div className="text-xs text-neutral-400 mb-2">Wat je leert — bullets</div>
            <StringList
              items={draft.sales_texts?.curriculum_bullets || []}
              onChange={(list) => setField(["sales_texts", "curriculum_bullets"], list)}
              placeholder="Nieuwe bullet"
              testid="curriculum-bullets"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Field label="Final CTA — titel">
              <input type="text" className={inputCls} value={draft.sales_texts?.final_cta_title || ""} onChange={(e) => setField(["sales_texts", "final_cta_title"], e.target.value)} data-testid="sales-final-title" />
            </Field>
            <Field label="Final CTA — body">
              <input type="text" className={inputCls} value={draft.sales_texts?.final_cta_body || ""} onChange={(e) => setField(["sales_texts", "final_cta_body"], e.target.value)} data-testid="sales-final-body" />
            </Field>
          </div>
        </Section>

        {/* Trainer */}
        <Section title="Trainer (Over Pattern Texture)" testid="section-trainer">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Naam">
              <input type="text" className={inputCls} value={draft.trainer?.name || ""} onChange={(e) => setField(["trainer", "name"], e.target.value)} data-testid="trainer-name" />
            </Field>
            <Field label="Rol">
              <input type="text" className={inputCls} value={draft.trainer?.role || ""} onChange={(e) => setField(["trainer", "role"], e.target.value)} data-testid="trainer-role" />
            </Field>
            <Field label="Introductie" full>
              <textarea rows={3} className={inputCls} value={draft.trainer?.intro || ""} onChange={(e) => setField(["trainer", "intro"], e.target.value)} data-testid="trainer-intro" />
            </Field>
            <Field label="Passie / missie" full>
              <textarea rows={3} className={inputCls} value={draft.trainer?.passion || ""} onChange={(e) => setField(["trainer", "passion"], e.target.value)} data-testid="trainer-passion" />
            </Field>
            <Field label="Foto URL" full>
              <input type="text" className={inputCls} value={draft.trainer?.photo || ""} onChange={(e) => setField(["trainer", "photo"], e.target.value)} data-testid="trainer-photo-url" />
            </Field>
          </div>
        </Section>

        {/* FAQ */}
        <Section title="Veelgestelde vragen (FAQ)" testid="section-faq">
          <FaqEditor
            items={draft.faq || []}
            onChange={(list) => setField(["faq"], list)}
          />
        </Section>

        {/* WhatsApp */}
        <Section title="WhatsApp instellingen" testid="section-whatsapp">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Telefoonnummer (internationaal, zonder +)">
              <input type="text" className={inputCls} value={draft.whatsapp?.phone || ""} onChange={(e) => setField(["whatsapp", "phone"], e.target.value)} placeholder="31621278150" data-testid="wa-phone" />
            </Field>
            <Field label="Standaard-bericht">
              <input type="text" className={inputCls} value={draft.whatsapp?.default_message || ""} onChange={(e) => setField(["whatsapp", "default_message"], e.target.value)} data-testid="wa-message" />
            </Field>
          </div>
        </Section>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          content={draft}
          device={previewDevice}
          setDevice={setPreviewDevice}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Publish confirm dialog */}
      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !publishing && setShowPublishConfirm(false)}>
          <div className="bg-neutral-950 border border-neutral-800 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="publish-confirm-dialog">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF6A1A]/15 border border-[#FF6A1A]/30 flex items-center justify-center shrink-0">
                <Rocket size={18} className="text-[#FF6A1A]" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-lg">Nu live zetten?</div>
                <p className="text-sm text-neutral-400 mt-1">Deze wijzigingen worden direct zichtbaar voor alle bezoekers van de homepage.</p>
              </div>
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={() => setShowPublishConfirm(false)} disabled={publishing} className="text-sm bg-neutral-800 hover:bg-neutral-700 text-neutral-200 px-4 py-2 rounded-full disabled:opacity-50" data-testid="publish-cancel">
                Annuleren
              </button>
              <button onClick={publish} disabled={publishing} className="text-sm bg-[#FF6A1A] text-black font-bold px-4 py-2 rounded-full flex items-center gap-1.5 disabled:opacity-50" data-testid="publish-confirm">
                {publishing ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} Ja, publiceer nu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Only send valid CMS payload fields (backend model whitelists them anyway).
function pickPayload(d) {
  return {
    hero: d.hero,
    courses: d.courses,
    trainer: d.trainer,
    sales_texts: d.sales_texts,
    faq: d.faq,
    announcement: d.announcement,
    whatsapp: d.whatsapp,
  };
}

const inputCls = "w-full bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF6A1A] text-neutral-100";

function Section({ title, children, testid }) {
  return (
    <details className="bg-neutral-900/50 border border-neutral-800 rounded-2xl overflow-hidden" open data-testid={testid}>
      <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-neutral-900/80">
        <span className="font-semibold text-sm">{title}</span>
        <span className="text-neutral-500 text-xs">Klik om in/uit te klappen</span>
      </summary>
      <div className="px-5 pb-5 pt-1">{children}</div>
    </details>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] tracking-wide uppercase text-neutral-400 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-10 h-6 rounded-full transition-colors relative ${checked ? "bg-[#FF6A1A]" : "bg-neutral-700"}`}
      data-testid={testid}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
    </button>
  );
}

function StringList({ items, onChange, placeholder, testid, max }) {
  const canAdd = !max || items.length < max;
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            className={inputCls}
            value={it}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            data-testid={`${testid}-${i}`}
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="shrink-0 w-9 h-9 rounded-lg bg-neutral-800 hover:bg-red-900/40 text-neutral-400 hover:text-red-300 flex items-center justify-center"
            data-testid={`${testid}-remove-${i}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {canAdd && (
        <button
          type="button"
          onClick={() => onChange([...items, ""])}
          className="text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full flex items-center gap-1.5"
          data-testid={`${testid}-add`}
        >
          <Plus size={12} /> {placeholder}
        </button>
      )}
    </div>
  );
}

function FaqEditor({ items, onChange }) {
  const add = () => onChange([...items, { q: "", a: "" }]);
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const update = (i, k, v) => {
    const next = items.map((it, j) => (j === i ? { ...it, [k]: v } : it));
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="faq-editor">
      {items.length === 0 && (
        <div className="text-xs text-neutral-500">Nog geen vragen toegevoegd. Klik &quot;Vraag toevoegen&quot; om te starten.</div>
      )}
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-3" data-testid={`faq-item-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wide text-neutral-500">Vraag #{i + 1}</span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-xs text-neutral-400 hover:text-red-300 flex items-center gap-1"
              data-testid={`faq-remove-${i}`}
            >
              <Trash2 size={12} /> Verwijderen
            </button>
          </div>
          <input
            type="text"
            placeholder="Vraag"
            className={inputCls + " mb-2"}
            value={it.q || ""}
            onChange={(e) => update(i, "q", e.target.value)}
            data-testid={`faq-q-${i}`}
          />
          <textarea
            rows={3}
            placeholder="Antwoord"
            className={inputCls}
            value={it.a || ""}
            onChange={(e) => update(i, "a", e.target.value)}
            data-testid={`faq-a-${i}`}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full flex items-center gap-1.5"
        data-testid="faq-add"
      >
        <Plus size={12} /> Vraag toevoegen
      </button>
    </div>
  );
}

function PreviewModal({ content, device, setDevice, onClose }) {
  // Lock body scroll while open
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, []);

  const width = device === "desktop" ? "min(1100px, 100%)" : "420px";

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur z-50 flex flex-col" data-testid="preview-modal">
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-neutral-800 bg-neutral-950">
        <div className="text-xs sm:text-sm text-neutral-400">
          <span className="text-[#FF6A1A] font-semibold">Preview</span> — concept content (nog niet live)
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-neutral-900 border border-neutral-800 rounded-full p-0.5">
            <button
              type="button"
              onClick={() => setDevice("mobile")}
              className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 ${device === "mobile" ? "bg-[#FF6A1A] text-black font-semibold" : "text-neutral-300"}`}
              data-testid="preview-device-mobile"
            >
              <Smartphone size={12} /> Mobiel
            </button>
            <button
              type="button"
              onClick={() => setDevice("desktop")}
              className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 ${device === "desktop" ? "bg-[#FF6A1A] text-black font-semibold" : "text-neutral-300"}`}
              data-testid="preview-device-desktop"
            >
              <Monitor size={12} /> Desktop
            </button>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-800 hover:bg-neutral-700 text-neutral-200 flex items-center justify-center" data-testid="preview-close">
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto flex justify-center py-4 px-2 sm:py-6 sm:px-4 bg-neutral-900">
        <div style={{ width, maxWidth: "100%" }} className="bg-white shadow-2xl rounded-2xl overflow-hidden">
          <div className="pt-preview">
            <CourseSelection previewContent={content} />
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
